/**
 * All media across events, optionally enriched with EXIF.
 *
 * Scaling note: EXIF lives in per-photo sidecars, so enriching N photos costs N
 * fetches. The timeline does NOT need it (each item already carries a filename
 * timestamp from mediaItem), so it passes `wantExif: false`. The map needs GPS,
 * so it opts in — but the batch is CAPPED (EXIF_CAP) to keep the call count
 * bounded. Beyond the cap, a banner tells the user some photos may be missing
 * from the map; the real fix at tens-of-thousands scale is a server-side photo
 * index (DynamoDB) — deliberately deferred. See server/INFRASTRUCTURE.md.
 */
import { useMemo } from 'react';
import { useAllMedia } from './useAllMedia.ts';
import { useExifBatch } from './useExif.ts';
import { exifTimestamp } from '../lib/exif.ts';
import type { ExifData, MediaItem } from '../lib/types.ts';

/** Max sidecars fetched in one go (bounds the map's request count). */
export const EXIF_CAP = 1500;

export interface EnrichedMedia {
  items: MediaItem[];
  exifMap: Map<string, ExifData> | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** Items that have GPS coordinates (only when EXIF was requested). */
  geotagged: Array<MediaItem & { gps: { lat: number; lng: number } }>;
  /** True when more media exists than EXIF_CAP, so EXIF coverage is partial. */
  exifCapped: boolean;
}

export function useEnrichedMedia(wantExif = false): EnrichedMedia {
  const { data: media, isLoading, isError, error } = useAllMedia();

  const exifCapped = !!media && wantExif && media.length > EXIF_CAP;
  const keys = useMemo(
    () => (wantExif ? (media ?? []).slice(0, EXIF_CAP).map((m) => m.key) : []),
    [media, wantExif],
  );
  const { data: exifMap, isLoading: exifLoading } = useExifBatch(keys, wantExif && keys.length > 0);

  const items = useMemo(() => {
    if (!media) return [];
    if (!exifMap) return media;
    // Prefer EXIF capture time when available; otherwise keep the filename ts.
    return media.map((item) => {
      const ts = exifTimestamp(exifMap.get(item.key));
      return ts != null ? { ...item, timestamp: ts } : item;
    });
  }, [media, exifMap]);

  const geotagged = useMemo(() => {
    if (!exifMap) return [];
    const out: Array<MediaItem & { gps: { lat: number; lng: number } }> = [];
    for (const item of items) {
      const gps = exifMap.get(item.key)?.gps;
      if (gps) out.push({ ...item, gps });
    }
    return out;
  }, [items, exifMap]);

  return {
    items,
    exifMap,
    isLoading: isLoading || (wantExif && exifLoading),
    isError,
    error,
    geotagged,
    exifCapped,
  };
}
